<template>
  <FlashNotifications ref="flash" />

  <!-- Simple spinner shown while waiting for Google/Outlook OAuth redirect -->
  <div v-if="connectingVia && !setupOverlay.visible"
    class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white dark:bg-[#0d1117]">
    <svg class="animate-spin w-10 h-10 text-blue-500 mb-5" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
    </svg>
    <p class="text-sm font-semibold text-gray-600 dark:text-gray-300">
      Redirecting to {{ connectingVia === 'google' ? 'Google' : 'Office 365' }}...
    </p>
  </div>

  <!-- Calendar setup overlay (authorizing + syncing phases) -->
  <div v-if="setupOverlay.visible"
    class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white dark:bg-[#0d1117] px-6">

    <!-- Provider icon with pulsing ring -->
    <div class="relative mb-8">
      <div class="w-20 h-20 rounded-2xl bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 shadow-xl flex items-center justify-center">
        <GoogleIcon v-if="setupOverlay.provider === 'google'" class="w-12 h-12" />
        <OutlookIcon v-else class="w-11 h-11" />
      </div>
      <div v-if="setupOverlay.phase !== 'done'"
        class="absolute -inset-3 rounded-[22px] border-2 border-blue-400/40 animate-ping pointer-events-none">
      </div>
    </div>

    <!-- Title -->
    <h2 class="text-2xl font-bold text-gray-900 dark:text-white text-center mb-1.5">
      {{ setupOverlay.phase === 'done' ? "You're all set!" : 'Setting up your calendar' }}
    </h2>
    <p class="text-sm text-gray-400 dark:text-gray-500 text-center mb-9">
      {{ setupOverlay.phase === 'done' ? 'Your calendar is ready to use.' : 'This may take 10 to 20 seconds.' }}
    </p>

    <!-- Step list -->
    <div class="w-full max-w-xs space-y-3.5 mb-9">
      <div v-for="(step, i) in setupSteps" :key="i" class="flex items-center gap-3">
        <!-- Step icon -->
        <div :class="[
          'w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center',
          step.done  ? 'bg-green-500'       :
          step.active ? 'bg-blue-600'       :
                        'bg-gray-100 dark:bg-gray-700'
        ]">
          <!-- Done checkmark -->
          <svg v-if="step.done" class="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
          </svg>
          <!-- Active spinner -->
          <svg v-else-if="step.active" class="animate-spin w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          <!-- Pending dot -->
          <span v-else class="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-500"></span>
        </div>
        <span :class="[
          'text-sm font-medium',
          step.done   ? 'text-gray-700 dark:text-gray-300' :
          step.active ? 'text-blue-600 dark:text-blue-400' :
                        'text-gray-300 dark:text-gray-600'
        ]">{{ step.label }}</span>
      </div>
    </div>

    <!-- Progress bar -->
    <div class="w-full max-w-xs">
      <div class="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div class="h-full bg-blue-500 rounded-full transition-all duration-700 ease-out"
          :style="{ width: setupOverlay.progress + '%' }">
        </div>
      </div>
      <p class="text-xs text-gray-400 dark:text-gray-600 text-right mt-1.5">{{ Math.round(setupOverlay.progress) }}%</p>
    </div>

  </div>

  <!-- Delete confirmation modal -->
  <div v-if="showDeleteModal"
    class="fixed inset-0 z-40 flex items-end sm:items-center justify-center p-4"
    @click.self="cancelDelete">
    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="cancelDelete"></div>
    <div class="relative w-full max-w-md bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl overflow-hidden">

      <!-- Top accent bar -->
      <div class="h-1 bg-red-500"></div>

      <div class="p-6">
        <!-- Icon + title -->
        <div class="flex items-start gap-4 mb-5">
          <div class="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-red-50 dark:bg-red-900/20">
            <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0 pt-0.5">
            <h3 class="text-base font-bold text-gray-900 dark:text-white">Disconnect Calendar</h3>
            <p v-if="deleteTarget" class="text-sm text-gray-400 dark:text-gray-500 mt-0.5 truncate">{{ deleteTarget.identity }}</p>
          </div>
        </div>

        <!-- Consequence list -->
        <div class="rounded-xl border border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 divide-y divide-red-100 dark:divide-red-900/30 mb-5">
          <div class="flex items-start gap-3 px-4 py-3">
            <svg class="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            <p class="text-sm text-red-700 dark:text-red-300">
              All synced events from this calendar will be permanently removed.
            </p>
          </div>
          <div v-if="deleteTarget?.sub_calendars?.length" class="flex items-start gap-3 px-4 py-3">
            <svg class="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            <p class="text-sm text-red-700 dark:text-red-300">
              {{ deleteTarget.sub_calendars.length }} shared {{ deleteTarget.sub_calendars.length === 1 ? 'calendar' : 'calendars' }} connected to this account will also be removed.
            </p>
          </div>
          <div class="flex items-start gap-3 px-4 py-3">
            <svg class="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            <p class="text-sm text-red-700 dark:text-red-300">
              This action cannot be undone. You can reconnect the calendar at any time.
            </p>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-3">
          <button @click="cancelDelete"
            class="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors">
            Keep Calendar
          </button>
          <button @click="confirmDelete" :disabled="!!deletingId"
            class="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-xl transition-colors flex items-center justify-center gap-2">
            <svg v-if="deletingId" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ deletingId ? 'Disconnecting...' : 'Yes, Disconnect' }}
          </button>
        </div>
      </div>

    </div>
  </div>

  <!-- Page wrapper -->
  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ══════════════════ MOBILE ══════════════════ -->
    <div class="sm:hidden">

      <!-- Mobile header -->
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center justify-between px-4 h-14">
          <div class="flex items-center gap-2.5">
            <button v-if="showConnectView && calendars.length > 0"
              @click="showConnectView = false"
              class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 -ml-1">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">
              {{ showConnectView ? 'Add Calendar' : I18N.website_content.connected_calendar.heading }}
            </h1>
          </div>
          <button v-if="!showConnectView && !loading && !error"
            @click="showConnectView = true"
            class="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-600/25 active:scale-95 transition-transform">
            <svg class="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile content -->
      <div class="pb-24">

        <!-- Loading skeleton -->
        <template v-if="loading">
          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Your calendars</p>
          <div class="animate-pulse mx-4 space-y-3">
            <div v-for="i in skeletonCount" :key="i" class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-sm">
              <div class="flex items-center px-4 py-4 gap-3">
                <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                  <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
                  <!-- dot cluster row -->
                  <div class="flex items-center gap-2 pt-0.5">
                    <div class="flex -space-x-1.5">
                      <div v-for="j in 4" :key="j" class="w-3.5 h-3.5 rounded-full bg-gray-200 dark:bg-gray-700 ring-2 ring-white dark:ring-[#1C2128]"></div>
                    </div>
                    <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-16"></div>
                  </div>
                </div>
                <div class="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
                <div class="w-4 h-4 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Error state -->
        <template v-else-if="error">
          <div class="flex flex-col items-center justify-center py-24 text-center px-4">
            <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
              </svg>
            </div>
            <p class="font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</p>
            <p class="text-sm text-gray-400 mb-4">{{ error }}</p>
            <button @click="retry" class="text-blue-600 font-semibold text-sm">Try again</button>
          </div>
        </template>

        <!-- List view -->
        <template v-else-if="showList">
          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Your calendars</p>
          <div class="space-y-3 mx-4">
            <div v-for="cal in calendars" :key="cal.id"
              class="bg-white dark:bg-[#1C2128] rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-sm">

              <!-- Header row (always visible) -->
              <div @click="toggleExpand(cal.id)"
                class="flex items-center px-4 py-4 gap-3 cursor-pointer select-none active:bg-gray-50/60 dark:active:bg-gray-800/20">
                <div class="flex-shrink-0">
                  <GoogleIcon v-if="cal.type === 'GoogleCalendar'" class="w-10 h-10" />
                  <OutlookIcon v-else class="w-9 h-9" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-[15px] font-semibold text-gray-900 dark:text-white leading-snug">
                    {{ cal.type === 'GoogleCalendar' ? 'Google Calendar' : 'Office 365 Calendar' }}
                  </p>
                  <div class="flex items-center gap-1 mt-0.5">
                    <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ cal.identity }}</p>
                    <button type="button"
                      class="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      :title="copiedId === cal.id ? 'Copied' : 'Copy'"
                      @click.stop="copyText(cal.id, cal.identity)">
                      <svg v-if="copiedId === cal.id" class="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      <svg v-else class="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                    </button>
                  </div>
                  <!-- Sub-cal dot cluster -->
                  <div v-if="cal.sub_calendars?.length" class="flex items-center gap-2 mt-2">
                    <div class="flex items-center -space-x-1.5">
                      <span v-for="sub in cal.sub_calendars.slice(0, 4)" :key="sub.id"
                        class="w-3.5 h-3.5 rounded-full ring-2 ring-white dark:ring-[#1C2128] flex-shrink-0"
                        :style="{ backgroundColor: COLOR_MAP[sub.color] || sub.color || COLOR_MAP.peacock, filter: 'saturate(1.3) brightness(0.78)' }">
                      </span>
                    </div>
                    <span class="text-[11px] text-gray-400 font-medium">
                      {{ cal.sub_calendars.length }} {{ cal.sub_calendars.length === 1 ? 'calendar' : 'calendars' }}
                    </span>
                  </div>
                </div>
                <!-- Active toggle -->
                <button type="button" @click.stop="toggleActive(cal)"
                  :class="cal.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'"
                  class="relative flex-shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none">
                  <span :class="cal.active ? 'translate-x-5' : 'translate-x-0.5'"
                    class="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
                </button>
                <!-- Chevron -->
                <svg :class="['flex-shrink-0 w-4 h-4 text-gray-300 transition-transform duration-200', isExpanded(cal.id) ? 'rotate-180' : '']"
                  fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                </svg>
              </div>

              <!-- Expanded section -->
              <div v-if="isExpanded(cal.id)">

                <!-- Sub-calendars -->
                <template v-if="cal.sub_calendars?.length">
                  <div class="border-t border-gray-100 dark:border-gray-700 px-4 pt-3 pb-1">
                    <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Shared Calendars</p>
                  </div>
                  <div v-for="sub in cal.sub_calendars" :key="sub.id"
                    class="flex items-center gap-3 px-4 py-3 pl-5 border-t border-gray-50 dark:border-gray-800/60">
                    <span class="w-5 h-5 rounded-full flex-shrink-0 shadow-sm ring-1 ring-black/10"
                      :style="{ backgroundColor: COLOR_MAP[sub.color] || sub.color || COLOR_MAP.peacock, filter: 'saturate(1.3) brightness(0.78)' }"></span>
                    <p class="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate min-w-0 font-medium">
                      {{ sub.display_name || formatCalendarId(sub.google_calendar_id) }}
                    </p>
                    <button type="button"
                      class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      :title="copiedId === sub.id ? 'Copied' : 'Copy'"
                      @click.stop="copyText(sub.id, sub.display_name || formatCalendarId(sub.google_calendar_id))">
                      <svg v-if="copiedId === sub.id" class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      <svg v-else class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                    </button>
                    <button type="button" @click="toggleSubActive(cal, sub)"
                      :class="sub.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'"
                      class="relative flex-shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none">
                      <span :class="sub.active ? 'translate-x-5' : 'translate-x-0.5'"
                        class="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
                    </button>
                  </div>
                </template>

                <!-- Action footer -->
                <div class="flex items-center px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/20 gap-1">
                  <!-- Sync -->
                  <button @click.stop="syncCalendar(cal)"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors">
                    <svg :class="{ 'animate-spin': syncingId === cal.id }"
                      class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
                    </svg>
                    <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Sync</span>
                  </button>

                  <span class="flex-1 text-right text-[10px] text-gray-400 truncate px-1">{{ cal.formatted_last_synced_at }}</span>

                  <div class="w-px h-4 bg-gray-200 dark:bg-gray-600 mx-0.5"></div>

                  <!-- Disconnect -->
                  <button @click.stop="openDelete(cal)"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <svg class="w-3.5 h-3.5 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"/>
                    </svg>
                    <span class="text-xs font-medium text-red-500">Disconnect</span>
                  </button>
                </div>

              </div>

            </div>
          </div>
        </template>

        <!-- Connect view -->
        <template v-else>
          <div class="px-4 pt-6 pb-3">
            <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Connect a calendar</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ parseLocale(I18N.website_content.connected_calendar.subheading2, { application_name: APPLICATION_NAME }) }}
            </p>
          </div>

          <!-- Active providers -->
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
            <!-- Google -->
            <div class="flex items-center px-4 py-4 gap-3">
              <GoogleIcon class="w-9 h-9 flex-shrink-0" />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">Google {{ I18N.website_content.connected_calendar.calendar }}</p>
                <p class="text-xs text-gray-400">Gmail, G Suite</p>
              </div>
              <button @click="connectGoogle"
                class="flex-shrink-0 inline-flex items-center justify-center w-[120px] py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-colors">
                {{ I18N.website_content.connected_calendar.connect_to }}
              </button>
            </div>
            <!-- Office 365 -->
            <div class="flex items-center px-4 py-4 gap-3">
              <OutlookIcon class="w-8 h-8 flex-shrink-0" />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">Office 365 {{ I18N.website_content.connected_calendar.calendar }}</p>
                <p class="text-xs text-gray-400">Outlook.com, live.com, hotmail</p>
              </div>
              <button @click="connectOutlook"
                class="flex-shrink-0 inline-flex items-center justify-center w-[120px] py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-colors">
                {{ I18N.website_content.connected_calendar.connect_to }}
              </button>
            </div>
          </div>

          <!-- Coming soon -->
          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Coming soon</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
            <div v-for="provider in comingSoonProviders" :key="provider.name"
              class="flex items-center px-4 py-4 gap-3 opacity-60">
              <div class="w-9 h-9 flex-shrink-0 flex items-center justify-center">
                <component :is="provider.icon" class="w-8 h-8" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ provider.name }}</p>
                <p class="text-xs text-gray-400">{{ provider.description }}</p>
              </div>
              <span class="flex-shrink-0 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-400 text-xs font-semibold rounded-xl">
                {{ I18N.website_content.connected_calendar.coming_soon }}
              </span>
            </div>
          </div>
        </template>

      </div>
    </div>
    <!-- ══════════════════ END MOBILE ══════════════════ -->


    <!-- ══════════════════ DESKTOP ══════════════════ -->
    <div class="hidden sm:flex sm:flex-col sm:h-screen">

      <!-- Desktop header -->
      <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between px-6 py-3.5">
          <div class="flex items-center gap-3">
            <button v-if="showConnectView && calendars.length > 0"
              @click="showConnectView = false"
              class="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors mr-1">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
              Back
            </button>
            <div>
              <div class="flex items-center gap-2.5">
                <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">
                  {{ showConnectView ? 'Add Calendar' : I18N.website_content.connected_calendar.heading }}
                </h1>
                <span v-if="!loading && !showConnectView && calendars.length > 0"
                  class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
                  {{ calendars.length }}
                </span>
              </div>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">
                {{ showConnectView
                  ? parseLocale(I18N.website_content.connected_calendar.subheading2, { application_name: APPLICATION_NAME })
                  : 'Sync your external calendars to prevent double bookings.' }}
              </p>
            </div>
          </div>
          <button v-if="!showConnectView && !loading && !error"
            @click="showConnectView = true"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            {{ I18N.website_content.connected_calendar.connect_calendar }}
          </button>
        </div>
      </div>

      <!-- Desktop scrollable content -->
      <div class="flex-1 overflow-y-auto px-6 py-6">
        <div class="max-w-3xl mx-auto">

          <!-- Loading skeleton -->
          <template v-if="loading">
            <div class="animate-pulse space-y-3">
              <div v-for="i in skeletonCount" :key="i"
                class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                <div class="flex items-center gap-4 px-6 py-5">
                  <div class="w-11 h-11 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
                  <div class="flex-1 space-y-2">
                    <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-2/5"></div>
                    <!-- dot cluster row -->
                    <div class="flex items-center gap-2 pt-1">
                      <div class="flex -space-x-1.5">
                        <div v-for="j in 5" :key="j" class="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 ring-2 ring-white dark:ring-[#1C2128]"></div>
                      </div>
                      <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-20"></div>
                    </div>
                  </div>
                  <div class="w-11 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
                  <div class="w-4 h-4 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0"></div>
                </div>
              </div>
            </div>
          </template>

          <!-- Error state -->
          <template v-else-if="error">
            <div class="flex flex-col items-center justify-center py-32 text-center">
              <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
                </svg>
              </div>
              <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
              <button @click="retry"
                class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
                Try again
              </button>
            </div>
          </template>

          <!-- List view -->
          <template v-else-if="showList">
            <div class="space-y-3">
              <div v-for="cal in calendars" :key="cal.id"
                class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">

                <!-- Collapsed header (always visible) -->
                <div @click="toggleExpand(cal.id)"
                  class="group flex items-center gap-4 px-6 py-5 cursor-pointer select-none hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-colors">

                  <!-- Provider icon -->
                  <div class="flex-shrink-0">
                    <GoogleIcon v-if="cal.type === 'GoogleCalendar'" class="w-11 h-11" />
                    <OutlookIcon v-else class="w-10 h-10" />
                  </div>

                  <!-- Name + email + dot cluster -->
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">
                      {{ cal.type === 'GoogleCalendar' ? 'Google Calendar' : 'Office 365 Calendar' }}
                    </p>
                    <div class="flex items-center gap-1 mt-0.5">
                      <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ cal.identity }}</p>
                      <button type="button"
                        class="flex-shrink-0 w-5 h-5 hidden group-hover:flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        :title="copiedId === cal.id ? 'Copied' : 'Copy'"
                        @click.stop="copyText(cal.id, cal.identity)">
                        <svg v-if="copiedId === cal.id" class="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                        <svg v-else class="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                      </button>
                    </div>
                    <!-- Sub-calendar dot cluster preview -->
                    <div v-if="cal.sub_calendars?.length" class="flex items-center gap-2 mt-2">
                      <div class="flex items-center -space-x-1.5">
                        <span v-for="sub in cal.sub_calendars.slice(0, 5)" :key="sub.id"
                          class="w-4 h-4 rounded-full ring-2 ring-white dark:ring-[#1C2128] flex-shrink-0"
                          :style="{ backgroundColor: COLOR_MAP[sub.color] || sub.color || COLOR_MAP.peacock, filter: 'saturate(1.3) brightness(0.78)' }">
                        </span>
                      </div>
                      <span class="text-xs text-gray-400 font-medium">
                        {{ cal.sub_calendars.length }} {{ cal.sub_calendars.length === 1 ? 'calendar' : 'calendars' }}
                      </span>
                    </div>
                  </div>

                  <!-- Active toggle -->
                  <button type="button" @click.stop="toggleActive(cal)"
                    :class="cal.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'"
                    :title="cal.active ? 'Active' : 'Inactive'"
                    class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                    <span :class="cal.active ? 'translate-x-5' : 'translate-x-0.5'"
                      class="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
                  </button>

                  <!-- Expand chevron -->
                  <svg :class="['flex-shrink-0 w-4 h-4 text-gray-300 transition-transform duration-200', isExpanded(cal.id) ? 'rotate-180' : '']"
                    fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                  </svg>
                </div>

                <!-- Expanded section -->
                <div v-if="isExpanded(cal.id)">

                  <!-- Sub-calendars -->
                  <template v-if="cal.sub_calendars?.length">
                    <div class="border-t border-gray-100 dark:border-gray-700 px-6 pt-4 pb-1">
                      <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Shared Calendars</p>
                    </div>
                    <div v-for="sub in cal.sub_calendars" :key="sub.id"
                      class="group flex items-center gap-4 px-6 py-3.5 border-t border-gray-50 dark:border-gray-800/60 hover:bg-gray-50/40 dark:hover:bg-gray-800/20 transition-colors">
                      <!-- Big color dot -->
                      <span class="w-5 h-5 rounded-full flex-shrink-0 shadow-sm ring-1 ring-black/10 dark:ring-white/10"
                        :style="{ backgroundColor: COLOR_MAP[sub.color] || sub.color || COLOR_MAP.peacock, filter: 'saturate(1.3) brightness(0.78)' }"></span>
                      <!-- Name -->
                      <p class="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 truncate min-w-0">
                        {{ sub.display_name || formatCalendarId(sub.google_calendar_id) }}
                      </p>
                      <!-- Copy -->
                      <button type="button"
                        class="flex-shrink-0 w-6 h-6 hidden group-hover:flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        :title="copiedId === sub.id ? 'Copied' : 'Copy'"
                        @click.stop="copyText(sub.id, sub.display_name || formatCalendarId(sub.google_calendar_id))">
                        <svg v-if="copiedId === sub.id" class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                        <svg v-else class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                        </svg>
                      </button>
                      <!-- Toggle -->
                      <button type="button" @click="toggleSubActive(cal, sub)"
                        :class="sub.active ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'"
                        :title="sub.active ? 'Deactivate' : 'Activate'"
                        class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
                        <span :class="sub.active ? 'translate-x-5' : 'translate-x-0.5'"
                          class="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200" />
                      </button>
                    </div>
                  </template>

                  <!-- Action footer -->
                  <div class="flex items-center gap-1 px-6 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/20">
                    <!-- Sync -->
                    <button @click.stop="syncCalendar(cal)"
                      :title="I18N.website_content.connected_calendar.sync"
                      class="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors">
                      <svg :class="{ 'animate-spin': syncingId === cal.id }"
                        class="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
                      </svg>
                      <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Sync now</span>
                    </button>

                    <!-- Last sync -->
                    <span class="flex-1 text-right text-[11px] text-gray-400 truncate px-2">
                      {{ I18N.website_content.connected_calendar.last_sync }}: {{ cal.formatted_last_synced_at }}
                    </span>

                    <div class="w-px h-4 bg-gray-200 dark:bg-gray-600 mx-1"></div>

                    <!-- Disconnect -->
                    <button @click.stop="openDelete(cal)"
                      class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"/>
                      </svg>
                      Disconnect
                    </button>
                  </div>

                </div>
              </div>
            </div>
          </template>

          <!-- Connect view (desktop) -->
          <template v-else>
            <div class="space-y-3">
              <h2 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                {{ I18N.website_content.connected_calendar.select_calendar }}
              </h2>

              <!-- Active providers -->
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                <!-- Google -->
                <div class="flex items-center px-6 py-5 gap-4">
                  <div class="flex items-center justify-center w-12 h-12 rounded-full bg-white border border-gray-100 dark:border-gray-700 shadow-sm flex-shrink-0">
                    <GoogleIcon class="w-7 h-7" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">Google {{ I18N.website_content.connected_calendar.calendar }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">Gmail, G Suite</p>
                  </div>
                  <button @click="connectGoogle"
                    class="flex-shrink-0 inline-flex items-center justify-center gap-2 w-48 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
                    <GoogleIcon class="w-4 h-4" />
                    {{ I18N.website_content.connected_calendar.connect_to }} Google
                  </button>
                </div>

                <!-- Office 365 -->
                <div class="flex items-center px-6 py-5 gap-4">
                  <div class="flex items-center justify-center w-12 h-12 rounded-full bg-white border border-gray-100 dark:border-gray-700 shadow-sm flex-shrink-0">
                    <OutlookIcon class="w-7 h-7" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">Office 365 {{ I18N.website_content.connected_calendar.calendar }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">Outlook.com, live.com, {{ I18N.website_content.connected_calendar.or }} hotmail {{ I18N.website_content.connected_calendar.calendar }}</p>
                  </div>
                  <button @click="connectOutlook"
                    class="flex-shrink-0 inline-flex items-center justify-center gap-2 w-48 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
                    <OutlookIcon class="w-4 h-4" />
                    {{ I18N.website_content.connected_calendar.connect_to }} Office 365
                  </button>
                </div>
              </div>

              <!-- Coming soon -->
              <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider pt-4 pb-2">Coming soon</p>
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                <div v-for="provider in comingSoonProviders" :key="provider.name"
                  class="flex items-center px-6 py-5 gap-4 opacity-50">
                  <div class="flex items-center justify-center w-12 h-12 rounded-full bg-white border border-gray-100 dark:border-gray-700 shadow-sm flex-shrink-0">
                    <component :is="provider.icon" class="w-7 h-7" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ provider.name }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">{{ provider.description }}</p>
                  </div>
                  <span class="flex-shrink-0 px-4 py-2 bg-gray-100 dark:bg-gray-700/60 text-gray-400 text-xs font-semibold rounded-lg">
                    {{ I18N.website_content.connected_calendar.coming_soon }}
                  </span>
                </div>
              </div>
            </div>
          </template>

        </div>
      </div>

    </div>
    <!-- ══════════════════ END DESKTOP ══════════════════ -->

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineComponent, h } from 'vue'
import { useConnectedCalendarsStore } from 'stores/connected_calendars.js'
import { useEventsStore } from 'stores/events.js'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { GoogleIcon, OutlookIcon } from 'components/icons/calendar_provider_icons.js'
import { getCachedConnectedCalendarsCount } from 'composables/useConnectedCalendarsCountCache.js'

const OUTLOOK_AUTH_STATE = 'outlook_timelydo_12345'

const COLOR_MAP = {
  tomato:    '#d50000',
  flamingo:  '#e67c73',
  tangerine: '#f4511e',
  banana:    '#f6bf26',
  sage:      '#33b679',
  basil:     '#0b8043',
  peacock:   '#039be5',
  blueberry: '#3f51b5',
  lavender:  '#7986cb',
  grape:     '#8e24aa',
  graphite:  '#616161',
}

// ── Inline SVG icon components (Google/Outlook shared via components/icons/calendar_provider_icons.js) ──
const ExchangeIcon = defineComponent({
  render() {
    return h('svg', { class: 'w-7 h-7 fill-blue-600', xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 512 512' }, [
      h('path', { d: 'M512 256c0 141.4-114.6 256-256 256S0 397.4 0 256 114.6 0 256 0s256 114.6 256 256zM256 48C141.1 48 48 141.1 48 256s93.1 208 208 208 208-93.1 208-208S370.9 48 256 48zm-96 192c0-53 43-96 96-96s96 43 96 96-43 96-96 96-96-43-96-96z' }),
    ])
  },
})

const ICloudIcon = defineComponent({
  render() {
    return h('svg', { class: 'w-7 h-7 fill-gray-500', xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 640 512' }, [
      h('path', { d: 'M0 336c0 79.5 64.5 144 144 144H512c70.7 0 128-57.3 128-128c0-61.9-44-113.6-102.4-125.4c4.1-10.7 6.4-22.4 6.4-34.6c0-53-43-96-96-96c-19.7 0-38.1 6-53.3 16.2C367 64.2 315.3 32 256 32C167.6 32 96 103.6 96 192c0 2.7 .1 5.4 .2 8.1C40.2 219.8 0 273.2 0 336z' }),
    ])
  },
})

// ── Composables ──
const flash = ref(null)
const store = useConnectedCalendarsStore()
const eventsStore = useEventsStore()
const { axiosRequest } = useRequest(flash)
const { I18N, APPLICATION_NAME, parseLocale } = useConstants()

// Real count from the last successful load, read synchronously so the very
// first paint already shows the right number of skeleton cards. See
// docs/skeleton_count_caching.md.
const skeletonCount = Math.min(getCachedConnectedCalendarsCount(), 12)

// ── UI state ──
const showConnectView = ref(false)
const showDeleteModal  = ref(false)
const expandedIds      = ref(new Set())
const deleteTargetId   = ref(null)
const deleteTarget     = ref(null)
const deletingId       = ref(null)
const syncingId        = ref(null)
const connectingVia = ref(null) // 'google' | 'outlook' — simple spinner during redirect
const setupOverlay  = ref({ visible: false, provider: null, phase: 'authorizing', progress: 0 })
const copiedId = ref(null)
let copiedTimer = null
let pollCancelled = false

// ── Computed ──
const loading  = computed(() => store.loading)
const error    = computed(() => store.error)
const calendars = computed(() => store.calendars)
const showList  = computed(() => !loading.value && !error.value && calendars.value.length > 0 && !showConnectView.value)

const comingSoonProviders = [
  { name: 'Exchange Calendar',  icon: ExchangeIcon, description: 'Microsoft Exchange Server' },
  { name: 'Outlook Plug-in',    icon: OutlookIcon,  description: 'Outlook 2007 and higher with Windows 7 and higher' },
  { name: 'iCloud Calendar',    icon: ICloudIcon,   description: 'Apple iCloud, available on all Apple products' },
]

// ── Expand/collapse ──
function toggleExpand(id) {
  const next = new Set(expandedIds.value)
  next.has(id) ? next.delete(id) : next.add(id)
  expandedIds.value = next
}

function isExpanded(id) {
  return expandedIds.value.has(id)
}

// ── Actions ──
async function toggleActive(cal) {
  const newActive = !cal.active
  store.updateCalendar(cal.id, { active: newActive })
  const res = await axiosRequest(
    'PUT',
    `/connected_calendars/${cal.id}/activate_or_deactivate`,
    { connected_calendar: { active: newActive } },
    true,
  )
  if (res.js_error || !res.response.success) {
    store.updateCalendar(cal.id, { active: !newActive })
  } else {
    eventsStore.invalidateNewFormData()
  }
}

async function toggleSubActive(parent, sub) {
  const newActive = !sub.active
  store.updateSubCalendar(parent.id, sub.id, { active: newActive })
  const res = await axiosRequest(
    'PUT',
    `/connected_calendars/${sub.id}/activate_or_deactivate`,
    { connected_calendar: { active: newActive } },
    true,
  )
  if (res.js_error || !res.response.success) {
    store.updateSubCalendar(parent.id, sub.id, { active: !newActive })
  } else {
    eventsStore.invalidateNewFormData()
  }
}

async function copyText(id, text) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    copiedId.value = id
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => { copiedId.value = null }, 2000)
  } catch {
    // Clipboard unavailable (permissions / insecure context) — leave icon as-is
  }
}

function formatCalendarId(id) {
  if (!id || id === 'primary') return 'Primary'
  const local = id.split('@')[0]
  return local.replace(/[._-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

async function syncCalendar(cal) {
  syncingId.value = cal.id
  const res = await axiosRequest('PUT', `/connected_calendars/${cal.id}/sync`, {}, true)
  if (!res.js_error && res.response.success) {
    store.updateCalendar(cal.id, { formatted_last_synced_at: res.response.data.last_synced_at })
  }
  syncingId.value = null
}

function openDelete(cal) {
  deleteTargetId.value = cal.id
  deleteTarget.value   = cal
  showDeleteModal.value = true
}

function cancelDelete() {
  showDeleteModal.value = false
  deleteTargetId.value = null
  deleteTarget.value   = null
}

async function confirmDelete() {
  if (!deleteTargetId.value) return
  const id = deleteTargetId.value
  deletingId.value = id
  const res = await axiosRequest('DELETE', `/connected_calendars/${id}`, {}, true)
  if (!res.js_error && res.response.success) {
    store.removeCalendar(id)
    eventsStore.invalidateNewFormData()
    if (calendars.value.length === 0) showConnectView.value = false
  }
  showDeleteModal.value = false
  deleteTargetId.value = null
  deleteTarget.value   = null
  deletingId.value = null
}

// ── Setup overlay steps (driven by progress 0-100) ──
const setupSteps = computed(() => {
  const p = setupOverlay.value.progress
  return [
    { label: 'Authorizing your account',       done: p >= 20, active: p > 0  && p < 20 },
    { label: 'Fetching your calendars',         done: p >= 50, active: p >= 20 && p < 50 },
    { label: 'Syncing shared calendars',        done: p >= 80, active: p >= 50 && p < 80 },
    { label: 'Finalizing setup',               done: p >= 100, active: p >= 80 && p < 100 },
  ]
})

// Polls while the setup overlay is visible (max 20s), advancing progress milestones.
// Exits early once the job has run (last_synced_at becomes non-null on the primary calendar
// OR sub-calendars appear). After the overlay closes, a silent background poll continues.
async function startSyncPolling(provider) {
  setupOverlay.value = { visible: true, provider, phase: 'syncing', progress: 20 }

  const MILESTONES = [
    { at: 2000,  progress: 35 },
    { at: 5000,  progress: 50 },
    { at: 9000,  progress: 65 },
    { at: 13000, progress: 80 },
    { at: 17000, progress: 92 },
  ]
  const MAX_WAIT = 20000
  const POLL_INTERVAL = 2500
  const start = Date.now()
  let milestoneIdx = 0

  while (Date.now() - start < MAX_WAIT) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL))
    const elapsed = Date.now() - start

    while (milestoneIdx < MILESTONES.length && elapsed >= MILESTONES[milestoneIdx].at) {
      setupOverlay.value.progress = MILESTONES[milestoneIdx].progress
      milestoneIdx++
    }

    store.invalidate()
    await store.fetchIfStale()

    // last_synced_at is null until the job finishes; formatted_last_synced_at is always
    // a non-empty i18n string so it cannot be used as a completion signal.
    const hasSubs   = store.calendars.some(c => c.sub_calendars?.length > 0)
    const hasSynced = store.calendars.some(c => c.last_synced_at != null)
    if (hasSubs || hasSynced) {
      // Give the job 2.5 s more to finish writing all sub-calendars before the final fetch
      await new Promise(r => setTimeout(r, 2500))
      break
    }
  }

  store.invalidate()
  await store.fetchIfStale()
  setupOverlay.value = { ...setupOverlay.value, phase: 'done', progress: 100 }
  await new Promise(r => setTimeout(r, 1200))
  setupOverlay.value.visible = false

  // If the job is still running (slow queue), keep polling silently so the UI
  // updates without needing a manual page refresh.
  startBackgroundPoll()
}

// Silently re-fetches every 4 s for up to 60 s after the overlay closes.
// Stops as soon as sub-calendars appear, last_synced_at is set, or the component unmounts.
async function startBackgroundPoll() {
  const MAX = 60000
  const start = Date.now()
  while (!pollCancelled && Date.now() - start < MAX) {
    await new Promise(r => setTimeout(r, 4000))
    if (pollCancelled) break
    const already = store.calendars.some(c => c.sub_calendars?.length > 0 || c.last_synced_at != null)
    if (already) break
    store.invalidate()
    await store.fetchIfStale()
    if (store.calendars.some(c => c.sub_calendars?.length > 0 || c.last_synced_at != null)) break
  }
}

// ── OAuth connect ──
async function connectGoogle() {
  // Show only a simple redirect spinner — the fancy overlay runs in processOAuthCode
  connectingVia.value = 'google'
  const res = await axiosRequest('POST', '/connected_calendars/google_connect', {}, false)
  if (!res.js_error && res.response.success && res.response.message === 'consent_screen_url') {
    window.location.href = res.response.data.url
    return
  }
  flash.value?.show(res.response?.message || 'Connection failed. Please try again.', 'alert')
  connectingVia.value = null
}

async function connectOutlook() {
  connectingVia.value = 'outlook'
  const res = await axiosRequest('POST', '/connected_calendars/outlook_connect', { data: {} }, false)
  if (!res.js_error && res.response.success) {
    if (res.response.data?.url) {
      window.location.href = res.response.data.url
      return
    }
    if (res.response.data?.connected) {
      window.location.href = '/connected_calendars'
      return
    }
  }
  flash.value?.show('Connection failed. Please try again.', 'alert')
  connectingVia.value = null
}

async function processOAuthCode() {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  if (!code) return

  const isOutlook = params.get('state') === OUTLOOK_AUTH_STATE
  window.history.replaceState({}, '', '/connected_calendars')

  if (isOutlook) {
    setupOverlay.value = { visible: true, provider: 'outlook', phase: 'authorizing', progress: 10 }
    const res = await axiosRequest('POST', '/connected_calendars/outlook_connect', { data: { code } }, false)
    if (res.js_error || !res.response.success || !res.response.data?.connected) {
      setupOverlay.value.visible = false
      flash.value?.show('Failed to connect Outlook. Please try again.', 'alert')
    } else {
      await startSyncPolling('outlook')
    }
  } else {
    setupOverlay.value = { visible: true, provider: 'google', phase: 'authorizing', progress: 10 }
    const res = await axiosRequest('POST', '/connected_calendars/google_connect', { code }, false)
    if (!res.js_error && res.response.success) {
      if (res.response.message === 'calendar_connected') {
        await startSyncPolling('google')
      }
    } else {
      setupOverlay.value.visible = false
      flash.value?.show(res.response?.message || 'Failed to connect. Please try again.', 'alert')
    }
  }
}

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
}

onMounted(async () => {
  const params = new URLSearchParams(window.location.search)
  await processOAuthCode()
  await store.fetchIfStale()

  if (params.get('connect') === 'true') {
    showConnectView.value = true
    window.history.replaceState({}, '', '/connected_calendars')
  }
})

onUnmounted(() => {
  pollCancelled = true
})
</script>
